import React, {useState, useEffect, useCallback, useMemo} from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { encodeId } from '../utils/idMasker';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import 'yet-another-react-lightbox/styles.css';
import API_CONFIG from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import PaywallModal from '../components/paywall/PaywallModal';
import LoginModal from '../components/auth/LoginModal';
import RegisterModal from '../components/auth/RegisterModal';
import useContentAccess from '../hooks/useContentAccess';
import { FaFacebook, FaTwitter, FaLinkedin, FaUser, FaLock, FaChevronLeft, FaRobot } from 'react-icons/fa';
import './PublicPage.css';
import HeaderWrapper from "../components/common/HeaderWrapper";

const PublicPage = () => {
    const { page_slug, slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, user, isLoading: isAuthLoading } = useAuth();
    const { creditBalance } = useCredits();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showPaywallModal, setShowPaywallModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const imageServer = API_CONFIG.BASE_URL;
    const defaultCoverImage = useMemo(() => '/default-cover.jpg', []);

    // Use the reusable access hook
    const {
        hasAccess,
        accessChecked,
        checkAccess
    } = useContentAccess();

    const isCreator = user && page && page.creator && user.id === page.creator.id;

    // Get the full URL of the current page for sharing
    const getShareUrl = useCallback(() => {
        const baseUrl = window.location.origin;
        const currentPath = location.pathname;
        return `${baseUrl}${currentPath}`;
    }, [location.pathname]);

    // Handle social media sharing
    const handleShare = useCallback((platform) => {
        const shareUrl = getShareUrl();
        const encodedUrl = encodeURIComponent(shareUrl);
        const pageTitle = page?.title ? encodeURIComponent(page.title) : '';

        let shareLink = '';

        switch (platform) {
            case 'facebook':
                shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                break;
            case 'twitter':
                shareLink = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${pageTitle}`;
                break;
            case 'linkedin':
                shareLink = `https://www.linkedin.com/sharing/share-offsite/?u=${encodedUrl}`;
                break;
            default:
                return;
        }

        window.open(shareLink, '_blank', 'noopener,noreferrer');
    }, [getShareUrl, page]);

    // Handle page access check
    const handlePageAccess = useCallback(async () => {
        if (!page || !page.is_paid) return true;

        const isCreatorUser = user && page.creator && user.id === page.creator.id;
        const hasPurchaseAccess = await checkAccess(page.id, 'post', isCreatorUser);

        if (!hasPurchaseAccess && !isCreatorUser) {
            // Show the modal for unpurchased content
            setShowPaywallModal(true);
            return false;
        }
        return true;
    }, [page, user, checkAccess]);

    // Effect to load the public page data initially
    useEffect(() => {
        const loadPublicPage = async () => {
            try {
                setLoading(true);
                setError('');

                const pageApiUrl = `${API_CONFIG.API_URL}/${page_slug}/${slug}`;
                const pageResponse = await axios.get(pageApiUrl);
                const pageData = pageResponse.data;
                setPage(pageData);

            } catch (err) {
                console.error('Error loading public page:', err);

                if (err.response?.status === 404) {
                    setError('Page not found or not published');
                } else {
                    setError('Failed to load page');
                }
            } finally {
                setLoading(false);
            }
        };
        loadPublicPage();
    }, [page_slug, slug]);

    // Effect to check access after page is loaded
    useEffect(() => {
        if (page && page.is_paid && !isAuthLoading) {
            handlePageAccess();
        }
    }, [page, isAuthLoading, handlePageAccess]);

    const handlePurchaseSuccess = () => {
        // Re-check access after successful purchase
        if (page) {
            checkAccess(page.id, 'post');
        }
        setShowPaywallModal(false);
    };

    if (loading) {
        return (
            <div className="public-page-loading">
                <div className="spinner"></div>
                <p>Loading page...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-page-error">
                <h2>Page Error</h2>
                <p>{error}</p>
                <a href="/">Return to Home</a>
            </div>
        );
    }

    if (!page) {
        return (
            <div className="public-page-error">
                <h2>Page Not Found</h2>
                <p>The page you're looking for doesn't exist or isn't published.</p>
                <a href="/">Return to Home</a>
            </div>
        );
    }

    // Determine if paywall should be shown (the visual overlay)
    const shouldShowPaywallOverlay = () => {
        if (isCreator) return false;
        if (!page.is_paid) return false;
        if (hasAccess) return false;
        if (!accessChecked) return false;
        return true;
    };

    // Determine if content should be shown
    const shouldShowContent = () => {
        if (!page.is_paid || isCreator) return true;
        return hasAccess;
    };

    const displayCoverImage = page.cover_image_url ? imageServer + page.cover_image_url : defaultCoverImage;

    return (
        <>
            <Helmet>
                <title>{page.title} | YourPlat4orm</title>
                <meta name="description" content={page.description?.replace(/<[^>]*>/g, '').substring(0, 160) || 'YourPlat4orm: Discover your next favourite thing'} />
                <meta property="og:title" content={`${page.title} | YourPlat4orm`} />
                <meta property="og:description" content={page.description?.replace(/<[^>]*>/g, '').substring(0, 160) || 'YourPlat4orm: Discover your next favourite thing'} />
                <meta property="og:image" content={page.cover_image_url ? `${imageServer}${page.cover_image_url}` : `${window.location.origin}/assets/HomePageCover.png`} />
                <meta property="og:url" content={getShareUrl()} />
                <meta property="og:type" content="article" />

                <meta name="twitter:title" content={`${page.title} | YourPlat4orm`} />
                <meta name="twitter:description" content={page.description?.replace(/<[^>]*>/g, '').substring(0, 160) || 'YourPlat4orm: Discover your next favourite thing'} />
                <meta name="twitter:image" content={page.cover_image_url ? `${imageServer}${page.cover_image_url}` : `${window.location.origin}/assets/HomePageCover.png`} />

                {page.creator && (
                    <meta name="author" content={page.page_name} />
                )}
                {page.stats?.published_at && (
                    <meta property="article:published_time" content={page.stats.published_at} />
                )}
            </Helmet>
            <HeaderWrapper />
            {/* Breadcrumb Navigation */}
            <div className="breadcrumb-bar">
                {page.page_name && page.page_id && (
                    <Link
                        to={`/${page_slug}/post/${encodeId(page.page_id)}`}
                        className="parent-page-link"
                    >
                        <FaChevronLeft /> {page.page_name} Posts
                    </Link>
                )}
            </div>
            <div className="public-page-container">
                <div className="public-page">
                    {/* Hero Section */}
                    <div className="hero-section">
                        {page.cover_image_url && (
                            <div className="cover-image">
                                <img src={displayCoverImage} alt={page.title} />
                            </div>
                        )}

                        <div className="hero-content">
                            <h1 className="page-title">{page.title}</h1>

                            <div className="article-meta">
                                {page.creator && (
                                    <div className="author-info">
                                        <div className="author-avatar">
                                            <FaUser className="avatar-placeholder" />
                                        </div>
                                        <div className="author-details">
                                            {page.stats?.published_at && (
                                                <p className="publish-date">
                                                    {new Date(page.stats.published_at).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Show price for paid content */}
                                {page.is_paid && (
                                    <div className="content-price">
                                        <span className="price-tag">💰 {page.price} credits</span>
                                        {!hasAccess && !isCreator && (
                                            <span className="premium-badge">Premium Content</span>
                                        )}
                                    </div>
                                )}

                                {/* Show AI Score */}
                                {page.ai_score != null && (
                                    <div className="ai-score-display">
                                        <FaRobot className="ai-icon" />
                                        <span>AI Score: {page.ai_score}/5</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Paywall Overlay - Visual lock for premium content */}
                    {shouldShowPaywallOverlay() && (
                        <div className="paid-content-modal">
                            <div className="paid-content-notice">
                                <div className="premium-icon">💎</div>
                                <h3>Premium Content</h3>
                                <p className="premium-description">
                                    This exclusive content requires <strong>{page.price} credits</strong> to access.
                                </p>

                                {isAuthenticated ? (
                                    <div className="credit-info">
                                        <div className="credit-comparison">
                                            <div className="credit-item">
                                                <span className="label">Your Credits:</span>
                                                <span className="amount">{creditBalance}</span>
                                            </div>
                                            <div className="credit-item">
                                                <span className="label">Required:</span>
                                                <span className="amount">{page.price}</span>
                                            </div>
                                        </div>

                                        {creditBalance >= page.price ? (
                                            <button
                                                className="purchase-btn"
                                                onClick={() => setShowPaywallModal(true)}
                                            >
                                                🔓 Purchase Access ({page.price} credits)
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    className="purchase-btn buy-credits"
                                                    onClick={() => navigate('/credits', { state: { from: location.pathname } })}
                                                >
                                                    💳 Buy Credits
                                                </button>
                                                <p className="insufficient-notice">
                                                    You need <strong>{page.price - creditBalance} more credits</strong> to purchase this content.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="auth-required">
                                        <FaLock className="lock-icon" />
                                        <p>Sign up or log in to purchase and access premium content!</p>
                                        <button
                                            className="auth-btn"
                                            onClick={() => navigate('/login', { state: { from: location.pathname } })}
                                        >
                                            🚀 Get Started
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Main Content - Only show if user has access */}
                    {shouldShowContent() && (
                        <div className="main-content-wrapper">
                            <div className="page-content">
                                {page.description ? (
                                    <div className="rich-description-content">
                                        <div
                                            className="article-body"
                                            dangerouslySetInnerHTML={{ __html: page.description }}
                                        />
                                    </div>
                                ) : (
                                    <div className="no-content">
                                        <h3>No content available</h3>
                                        <p>This page doesn't have any published content yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Sidebar */}
                            <div className="content-sidebar">
                                <div className="sidebar-widget share-widget">
                                    <h4>Share this Article</h4>
                                    <div className="share-buttons">
                                        <button
                                            className="share-btn facebook"
                                            onClick={() => handleShare('facebook')}
                                            aria-label="Share on Facebook"
                                            title="Share on Facebook"
                                        >
                                            <FaFacebook /> Share
                                        </button>
                                        <button
                                            className="share-btn twitter"
                                            onClick={() => handleShare('twitter')}
                                            aria-label="Share on Twitter"
                                            title="Share on Twitter"
                                        >
                                            <FaTwitter /> Tweet
                                        </button>
                                        <button
                                            className="share-btn linkedin"
                                            onClick={() => handleShare('linkedin')}
                                            aria-label="Share on LinkedIn"
                                            title="Share on LinkedIn"
                                        >
                                            <FaLinkedin /> Post
                                        </button>
                                    </div>
                                </div>

                                {page.stats && (
                                    <div className="sidebar-widget stats-widget">
                                        <h4>Article Stats</h4>
                                        <ul className="stats-list">
                                            {page.stats.view_count !== undefined && (
                                                <li className="stat-item">👁️ <strong>{page.stats.view_count}</strong> views</li>
                                            )}
                                            {page.stats.like_count !== undefined && (
                                                <li className="stat-item">❤️ <strong>{page.stats.like_count}</strong> likes</li>
                                            )}
                                            {page.stats.comment_count !== undefined && (
                                                <li className="stat-item">💬 <strong>{page.stats.comment_count}</strong> comments</li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Page Footer */}
                    {shouldShowContent() && (
                        <div className="page-footer">
                            <div className="copyright-section">
                                <p>© {new Date().getFullYear()} {page.page_name}. All rights reserved.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Reusable Paywall Modal - This handles the actual purchase */}
                {page && page.is_paid && (
                    <PaywallModal
                        isOpen={showPaywallModal}
                        onClose={() => setShowPaywallModal(false)}
                        contentId={page.id}
                        contentType="post"
                        contentTitle={page.title}
                        price={page.price}
                        onPurchaseSuccess={handlePurchaseSuccess}
                        onLoginRequired={() => setShowLoginModal(true)}
                    />
                )}

                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onLoginSuccess={() => setShowLoginModal(false)}
                    onSwitchToRegister={() => {
                        setShowLoginModal(false);
                        setShowRegisterModal(true);
                    }}
                />
                <RegisterModal
                    isOpen={showRegisterModal}
                    onClose={() => setShowRegisterModal(false)}
                    onBackToLogin={() => {
                        setShowRegisterModal(false);
                        setShowLoginModal(true);
                    }}
                />
            </div>
        </>
    );
};

export default PublicPage;